import { formatMoney } from 'utils';

const PURCHASE_OPTION_INPUTS = '.bigdrop-purchase-options input[type="radio"], .appstle_sub_widget input[type="radio"]';

class BigDropPricePack {
  constructor(element) {
    this.element = element;
    this.priceElement = element.querySelector('[data-bigdrop-price]');
    this.packElement = element.querySelector('[data-bigdrop-pack]');
    this.saveElement = element.querySelector('[data-bigdrop-save]');
    this.compareElement = element.querySelector('[data-bigdrop-compare]');
    this.roundelElement = element.querySelector('[data-bigdrop-roundel]');
    const variantDataElement = element.querySelector('[data-bigdrop-price-pack-variants]');

    if (!variantDataElement) return;

    try {
      this.variantData = JSON.parse(variantDataElement.textContent);
    } catch (error) {
      console.warn('Big Drop price/pack: unable to parse variant data.', error);
      this.variantData = {};
    }

    this.currentVariantId = element.dataset.selectedVariantId;
    this.onVariantChange = this.onVariantChange.bind(this);
    this.onPurchaseOptionChange = this.onPurchaseOptionChange.bind(this);
    document.addEventListener('variant:changed', this.onVariantChange);
    document.addEventListener('change', this.onPurchaseOptionChange);
    const onQuantityChange = event => {
      if (!this.belongsToProduct(event.target)) return;
      if (event.target.matches('[name="quantity"], quantity-input') && this.currentPrice) {
        this.updateAddToBasketLabel(this.currentPrice);
      }
    };
    document.addEventListener('input', onQuantityChange);
    document.addEventListener('change', onQuantityChange);
    document.addEventListener('update', onQuantityChange, true);

    const initialVariant = this.variantData[this.currentVariantId];
    if (initialVariant) this.renderPrice(initialVariant, initialVariant);

    requestAnimationFrame(() => this.syncSelectedPurchaseOption());
    this.waitForPurchaseOptions();
  }

  // Appstle injects its widget after load, so sync once it appears.
  waitForPurchaseOptions() {
    const productBox = this.element.closest('.product-single__box');
    let attempts = 0;
    const timer = setInterval(() => {
      attempts += 1;
      if (productBox?.querySelector(PURCHASE_OPTION_INPUTS)) {
        clearInterval(timer);
        this.syncSelectedPurchaseOption();
      } else if (attempts > 40) {
        clearInterval(timer);
      }
    }, 250);
  }

  onVariantChange(event) {
    const variantId = event.detail?.variant?.id;
    if (!variantId) return;

    if (
      event.detail.sectionId &&
      this.element.dataset.sectionId &&
      event.detail.sectionId !== this.element.dataset.sectionId
    ) {
      return;
    }

    const variant = this.variantData[variantId];
    if (!variant) return;

    this.currentVariantId = String(variantId);
    this.renderPrice(variant, variant);

    requestAnimationFrame(() => this.syncSelectedPurchaseOption());
  }

  onPurchaseOptionChange(event) {
    const target = event.target;
    if (!this.belongsToProduct(target)) return;

    // The Appstle frequency dropdown changes the plan without a radio change.
    const input = target.matches?.('.appstle_select')
      ? target.closest('.appstle_subscription_wrapper_option')?.querySelector('input[type="radio"]')
      : target.closest?.(PURCHASE_OPTION_INPUTS);
    if (!input) return;

    requestAnimationFrame(() => this.renderPurchaseOption(input));
  }

  syncSelectedPurchaseOption() {
    const productBox = this.element.closest('.product-single__box');
    const selectedInput = productBox?.querySelector(`${PURCHASE_OPTION_INPUTS}:checked`);

    if (selectedInput) this.renderPurchaseOption(selectedInput);
  }

  belongsToProduct(input) {
    return input.closest('.product-single__box') === this.element.closest('.product-single__box');
  }

  renderPurchaseOption(input) {
    const variant = this.variantData[this.currentVariantId];
    if (!variant) return;

    const planId = this.getSellingPlanId(input);
    if (!planId) {
      this.renderPrice(variant, variant);
      return;
    }

    const sellingPlan = variant.sellingPlans?.[planId];
    if (sellingPlan) this.renderPrice(sellingPlan, variant);
  }

  // Appstle keeps the real plan id on the radio, or in the frequency dropdown.
  getSellingPlanId(input) {
    if (input.dataset.radioType === 'selling_plan') return input.dataset.sellingPlanId;
    if (!input.dataset.appstleRealValue) return null;

    const select = input.closest('.appstle_subscription_wrapper_option')?.querySelector('.appstle_select');
    return select?.value || input.dataset.appstleRealValue;
  }

  renderSaving(price, variant) {
    if (!this.saveElement) return;

    const saving = Math.round((1 - price.priceCents / variant.priceCents) * 100);
    const show = price !== variant && saving > 0;
    this.saveElement.textContent = show ? `Save ${saving}% with a subscription` : '';
    this.saveElement.hidden = !show;

    // Strike through the compare-at price when the product has one, otherwise
    // the one-time price while a subscription is selected.
    const hasCompareAt = Boolean(variant.compareAtCents);
    const struckPrice = hasCompareAt ? variant.compareAtPrice : show ? variant.price : '';
    if (this.compareElement) {
      this.compareElement.textContent = struckPrice;
      this.compareElement.hidden = !struckPrice;
    }

    // The roundel only appears when a compare-at price has been entered.
    if (this.roundelElement && !this.roundelElement.dataset.fixedText) {
      const discount = hasCompareAt ? Math.round((1 - price.priceCents / variant.compareAtCents) * 100) : 0;
      this.roundelElement.textContent = discount > 0 ? `${discount}% off` : '';
      this.roundelElement.hidden = discount <= 0;
    }
  }

  renderPrice(price, variant) {
    this.priceElement.textContent = price.price;
    this.renderSaving(price, variant);
    this.currentPrice = {
      available: variant.available,
      price: price.price,
      priceCents: price.priceCents,
    };
    this.updateAddToBasketLabel(this.currentPrice);

    if (!price.perCanPrice || !variant.packQuantity || !variant.packFormat) {
      this.packElement.hidden = true;
      this.packElement.textContent = '';
      return;
    }

    this.packElement.textContent = `${price.perCanPrice} a can - ${variant.packQuantity} x ${variant.packFormat}`;
    this.packElement.hidden = false;
  }

  updateAddToBasketLabel(variant) {
    if (!variant.available) return;

    const productBox = this.element.closest('.product-single__box');
    const buttonText = productBox?.querySelector(
      '.product-single__box__block--buy_buttons [name="add"] staged-action-text'
    );

    if (buttonText) {
      const quantity = Math.max(1, Number(productBox.querySelector('[name="quantity"]')?.value) || 1);
      const total = formatMoney(variant.priceCents * quantity, window.theme.money_format);
      buttonText.textContent = `ADD TO BASKET - ${total}`;
    }
  }
}

document.querySelectorAll('.bigdrop-price-row').forEach((element) => {
  new BigDropPricePack(element);
});
